import { useCallback, useState } from 'react';

import { Box, Dialog, Text } from '@opendoor/bricks-next';
import type { SavedSearchFieldsFragment } from '__generated__/athena';

import type { RemoveSavedSearchResult } from './useSavedSearches';

export interface ConfirmDeleteDialogProps {
  savedSearch: SavedSearchFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Deletes via the host's shared `useSavedSearches` instance so the dropdown's
   * list refetches and drops the row in place (a separate instance would only
   * reflect the removal on reload).
   */
  onConfirmDelete: (id: string) => Promise<RemoveSavedSearchResult>;
}

const GENERIC_ERROR = 'Failed to delete search. Please try again.';

export const ConfirmDeleteDialog = ({
  savedSearch,
  isOpen,
  onClose,
  onConfirmDelete,
}: ConfirmDeleteDialogProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    onClose();
  }, [submitting, onClose]);

  const handleConfirm = useCallback(async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await onConfirmDelete(savedSearch.id);
    setSubmitting(false);
    if (result.success) {
      onClose();
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_ERROR);
  }, [onConfirmDelete, savedSearch.id, submitting, onClose]);

  let confirmLabel = 'Delete';
  if (submitting) {
    confirmLabel = 'Deleting…';
  }

  return (
    <Dialog
      analyticsName="confirm-delete-saved-search-dialog"
      isOpen={isOpen}
      setIsOpen={handleClose}
      dismissable={!submitting}
    >
      <Box width="100%" $largerThanSM={{ width: 360 }}>
        <Dialog.Content
          title="Delete saved search?"
          description={`This will remove "${savedSearch.name}". You can’t undo this action.`}
        >
          {errorMessage && (
            <Box pt={12}>
              <Text tag="p" typography="$bodySmall" color="$contentStateErrorDefault" role="alert">
                {errorMessage}
              </Text>
            </Box>
          )}
        </Dialog.Content>
        <Dialog.Actions
          buttonOneProps={{
            analyticsName: 'confirm-delete-saved-search-cancel',
            label: 'Cancel',
            variant: 'secondary',
            onPress: handleClose,
            disabled: submitting,
          }}
          buttonTwoProps={{
            analyticsName: 'confirm-delete-saved-search-confirm',
            label: confirmLabel,
            variant: 'primary',
            onPress: handleConfirm,
            disabled: submitting,
            loading: submitting,
          }}
        />
      </Box>
    </Dialog>
  );
};
